type IntercomModule = typeof import('@intercom/messenger-js-sdk');
type IntercomSettings = Parameters<IntercomModule['default']>[0];

let bootPromise: Promise<IntercomModule | null> | null = null;
let bootedUserId: string | undefined;

const loadAndBoot = async (settings: IntercomSettings) => {
  try {
    const intercomModule = await import('@intercom/messenger-js-sdk');

    intercomModule.default(settings);

    return intercomModule;
  } catch {
    // A messenger that fails to load must not break the page.
    // Help then stays a no-op instead of throwing on click.
    return null;
  }
};

/**
 * Boots the messenger. This has to happen on page load rather than on the
 * first Help click. Outbound messages such as banners and tours are delivered
 * by the messenger runtime, so they never render until it is booted.
 *
 * The launcher stays hidden via `hide_default_launcher`, so booting early puts
 * nothing on screen by itself.
 */
export const bootIntercom = async (settings: IntercomSettings) => {
  if (!bootPromise) {
    bootedUserId = settings.user_id;
    bootPromise = loadAndBoot(settings);

    await bootPromise;
    return;
  }

  if (bootedUserId === settings.user_id) {
    return;
  }

  bootedUserId = settings.user_id;

  const intercomModule = await bootPromise;

  // The identity changed mid-session (a tenant switch, for example).
  // Shut down first so the previous user's conversations do not carry over.
  intercomModule?.shutdown();
  intercomModule?.boot(settings);
};

export const showIntercom = async () => {
  const intercomModule = await bootPromise;

  intercomModule?.show();
};
